import { observable } from 'mobx';
import type Context from '@/context';
import type {
  IChipModel,
  IChipInstanceModel,
  IChipStoreModel,
  IProjectModel,
} from '@/interfaces/models';
import type { ChipModelFactory } from '@/factories/ChipModelFactory';
import type { ChipId, ChipInstanceId } from '@/types/ids';
import type { ChipExportBundle } from '@/schemas/chip';
import {
  orderChipFileDefinitions,
  validateChipFileBundle,
} from './projectSnapshot/CurrentChipFormat';
import { Wap } from '@/utils/Wap/Wap';
import { recoverSavedLatches } from '@/models/logic/recoverSavedLatches';

/** File imports publish only after every new definition has hydrated and rebuilt.
 * The staging context uses the destination runtime ID scope; it never closes it.
 * Existing definitions are not replaced or refreshed by a chip-file import. */
export function importChipFileBundle(
  context: Context,
  factory: ChipModelFactory,
  input: ChipExportBundle,
): IChipModel[] {
  if (context.challenge && !context.challengeAuthoring)
    throw Error('Chip importing is disabled during challenge play.');
  const bundle = validateChipFileBundle(input);
  for (const chip of bundle.chips)
    if (context.chipStore.getChip(chip.id as ChipId))
      throw new Error(
        `Chip “${chip.name}” already exists. Import into a project without these chip IDs.`,
      );
  const existingInstances = new Set<string>();
  const reserveInstances = (chip: IChipModel) => {
    existingInstances.add(chip.editingInstance.id);
    for (const id of chip.logicCircuit.chipInstances.keys()) existingInstances.add(id);
  };
  for (const chip of context.chipStore.chips) reserveInstances(chip);
  // A cloned draft is not in chips and its restored instances need not be in the
  // project map yet. Its IDs still own live work and later draft cleanup uses them.
  for (const { draft } of context.app.chipDrafts.values()) reserveInstances(draft);
  for (const chip of bundle.chips)
    for (const instance of [...chip.chipInstanceDtos, chip.editingInstanceDto])
      if (
        existingInstances.has(instance.id) ||
        context.project.chipInstanceMap.has(instance.id as ChipInstanceId)
      )
        throw new Error('A chip instance ID already exists in this project.');
  const active = observable.box(false),
    staged = observable.map<ChipId, IChipModel>([], { deep: false });
  const instanceMap = new Wap<
    ChipInstanceId,
    import('@/interfaces/models').IChipInstanceModel
  >();
  // Empty targets avoid overriding MobX's non-configurable bound action slots.
  const project = new Proxy({} as IProjectModel, {
    get(_target, key) {
      return key === 'chipInstanceMap' && !active.get()
        ? instanceMap
        : Reflect.get(context.project, key);
    },
  });
  const store = new Proxy({} as IChipStoreModel, {
    get(_target, key) {
      if (key === 'getChip')
        return (id: ChipId) =>
          active.get() ? context.chipStore.getChip(id) : staged.get(id);
      if (key === 'chips')
        return active.get() ? context.chipStore.chips : [...staged.values()];
      return Reflect.get(context.chipStore, key);
    },
  });
  const stageContext = new Proxy({} as Context, {
    get(_target, key) {
      if (key === 'chipStore') return store;
      if (key === 'project') return project;
      if (key === 'isHydrating') return active.get() ? context.isHydrating : true;
      return Reflect.get(context, key);
    },
  });
  const created: IChipModel[] = [];
  const dependants = new Map<string, IChipInstanceModel[]>();
  try {
    // No active chip store or project instance-map publication during hydration.
    for (const dto of orderChipFileDefinitions(bundle.chips)) {
      const chip = factory.fromDto(stageContext, dto);
      created.push(chip);
      staged.set(chip.id, chip);
      for (const instance of chip.logicCircuit.chipInstances.values()) {
        instanceMap.set(instance.id, instance);
        const instances = dependants.get(instance.chipId) ?? [];
        instances.push(instance);
        dependants.set(instance.chipId, instances);
      }
    }
    // A parent snapshot can retain newer runtime than its placed-instance DTO,
    // including owner-factored captures with a distinct authoritative node object.
    // Reconcile exact persisted identities before refreshGraph replaces the saved
    // child graphs, matching project restoration rather than live numeric handles.
    for (const chip of created) {
      const savedByExternalId = new Map(
        [...chip.editingInstance.logicGraph.nodes.values()].map((node) => [
          node.idText,
          node,
        ]),
      );
      for (const instance of chip.logicCircuit.chipInstances.values()) {
        for (const node of instance.logicGraph.nodes.values()) {
          const saved = savedByExternalId.get(node.idText);
          if (saved) node.restoreRuntimeState(saved);
        }
      }
    }
    for (const chip of created) {
      chip.rebuildTemplateGraph();
      for (const instance of dependants.get(chip.id) ?? []) instance.refreshGraph();
    }
    for (const chip of created) {
      if (chip.editingInstance.logicGraph.nodes.size)
        chip.logicCircuit.graphManager.graph = chip.editingInstance
          .logicGraph as import('@/models/logic/LogicGraph').LogicGraph;
      chip.logicCircuit.rebuildGraph();
      const logic = chip.logicCircuit;
      if (recoverSavedLatches(logic.logicGraph)) {
        logic.logicSimulation.setGraph(logic.logicGraph);
        logic.logicSimulation.drainUntilStable(false);
        logic.logicSimulation.onPostUpdate?.(new Set(logic.logicGraph.nodes.keys()));
      }
    }
    for (const chip of created) context.chipStore.addChip(chip);
    for (const [id, instance] of instanceMap.entries())
      context.project.chipInstanceMap.set(id, instance);
    active.set(true);
    staged.clear();
    instanceMap.clear();
    return created;
  } catch (error) {
    for (const [id, instance] of instanceMap.entries())
      if (context.project.chipInstanceMap.get(id) === instance)
        context.project.chipInstanceMap.delete(id);
    for (const chip of created) {
      if (context.chipStore.getChip(chip.id) === chip)
        context.chipStore.deleteChip(chip.id);
      else {
        chip.logicCircuit.dispose?.();
        chip.logicCircuit.circuitModel.connectionModel.dispose();
        chip.structureCircuit.circuitModel.connectionModel.dispose();
      }
    }
    staged.clear();
    instanceMap.clear();
    throw error;
  }
}
